import { CategoryType, Pagination } from '@core/shared/domain';
import { ArticleSearchQueryField } from '@mp/shared/domain';
import type { AdditionalArticleStatus, ArticleStatus } from '@mpcm/shared';

/**
 * Basistyp für Suchabfragen.
 */
export interface ArticleSearchRequestBase {
  /**
   * Die Textfeldeingabe.
   */
  query?: string;

  /**
   * Liste die Lieferanten-IDs, auf die gefiltert wird.
   */
  suppliers?: string[];

  /**
   * Liste die Hersteller-IDs, auf die gefiltert wird.
   */
  manufacturers?: string[];

  /**
   * Liste der EClass-Merkmale, auf die gefiltert wird.
   */
  eClassFeatures?: string[];

  /**
   * Liste der Artikel-Merkmale, auf die gefiltert wird.
   */
  articleFeatures?: string[];

  /**
   * Liste der pharmazeutischen Merkmale, auf die gefiltert wird.
   */
  pharmaceuticalFeatures?: string[];

  /**
   * Filtert Artikel einer bestimmten Warengruppe.
   */
  category?: string;

  /**
   * Gibt an, welche Warengruppe angezeigt werden soll (Standard: `ECLASS`).
   */
  categoryType?: CategoryType;

  /**
   * Gibt an, welche Artikel angezeigt werden sollen (Standard: `All`).
   */
  articleType?: ArticleTypeFilter;

  /**
   * Gibt an, ob Artikel mit Matches angezeigt werden sollen (Standard: `All`).
   */
  articleMatch?: ArticleMatchFilter;

  /**
   * Gibt an, nach welchem Artikel-Feld in der Expertensuche gefiltert werden soll
   */
  field?: ArticleSearchQueryField;

  /**
   * Liste der Sortimente, auf die gefiltert wird.
   */
  articleRanges?: string[];

  /**
   * Status der Artikel, auf die gefiltert wird.
   */
  currentArticleStatus?: ArticleStatus[];

  /**
   * Date of current article status
   */
  currentArticleStatusDate?: string;

  /**
   * Future status of an article
   */
  futureArticleStatus?: ArticleStatus[];

  /**
   * Date of future article status
   */
  futureArticleStatusDate?: string;

  /**
   * Additional status of an article
   */
  additionalArticleStatus?: AdditionalArticleStatus[];

  /**
   * Date of additional article status
   */
  additionalArticleStatusDate?: string;
}

/**
 * Beschreibt die Abfrage für die Artikelsuche.
 */
export type ArticleSearchRequest = ArticleSearchRequestBase & Partial<Pagination>;

/**
 * Beschreibt eine Suchabfrage anhand einer Suchansicht.
 */
export interface ArticleSearchViewRequestBase {
  /**
   * Gibt an, welche Ansicht geladen werden soll.
   */
  searchViewId: string;
}
/**
 * Beschreibt eine Suchabfrage anhand einer Suchansicht.
 */
export type ArticleSearchViewRequest = ArticleSearchViewRequestBase & Partial<Pagination>;

/**
 * Enum für Artikeltyp-Filter in der Artikelsuche.
 */
export enum ArticleTypeFilter {
  /**
   * Alle Artikel anzeigen (kein Filter).
   */
  All = 'All',
  /**
   * Nur Klinikartikel anzeigen.
   */
  ClinicArticle = 'ClinicArticle',
  /**
   * Nur Industrieartikel anzeigen.
   */
  IndustryArticle = 'IndustryArticle',
}

/**
 * Enum für Match0Filter in der Artikelsuche.
 */
export enum ArticleMatchFilter {
  /**
   * Alle Artikel anzeigen (kein Filter).
   */
  All = 'All',
  /**
   * Nur Artikel mit Matches anzeigen.
   */
  WithMatch = 'WithMatch',
  /**
   * Nur Artikel ohne Matches anzeigen.
   */
  WithoutMatch = 'WithoutMatch',
}
